/**
 * The browser build's `posthog-js` (FRIDGE-1648). lib/analytics/posthog-browser-aliases.js
 * points the browser bundle's imports of `posthog-js` here, so client code (app/providers.tsx,
 * app/posthog-pageview.tsx, every capture site) and the jest tests that mock `posthog-js` stay
 * exactly as they were. Server rendering and jest keep the real package.
 *
 * This is the one place that reaches the real library: a dynamic import of the package's own
 * module file (no alias matches that path), started by deferred-posthog.ts once the page has
 * loaded. On fridge.com PostHog's property helpers start downloading right after first paint and
 * the library once the page has loaded, whether or not the provider has called init yet; until
 * the library arrives every call is kept, and an exit sends the captures by beacon. Anywhere
 * else (localhost, previews) the provider never calls init and nothing is downloaded.
 *
 * At init it announces lib/posthog-loaded.ts's signal: from then on a capture is kept, so the
 * captures that wait on that signal (the embed view, search outcomes) need not wait for the
 * library, and a visitor who leaves first still sends them in the exit beacon.
 */
import { announcePostHogLoaded } from '@/lib/posthog-loaded'
import { afterFirstPaint, afterPageLoad, createDeferredPostHog, type BeaconHelpers, type DeferredPostHog, type RealPostHogLike } from './deferred-posthog'

declare const __POSTHOG_JS_VERSION__: string | undefined

const hasWindow = typeof window !== 'undefined'

function storage(kind: 'localStorage' | 'sessionStorage') {
  try {
    return hasWindow ? window[kind] : null
  } catch {
    return null
  }
}

const createClient = (): DeferredPostHog => createDeferredPostHog({
  loadLibrary: () => import('posthog-js/dist/module.js').then((m) => m.default as unknown as RealPostHogLike),
  loadBeaconHelpers: () => import('./posthog-beacon-helpers').then((m): BeaconHelpers => m.beaconHelpers),
  afterFirstPaint: (run) => {
    if (hasWindow) afterFirstPaint(run, window)
  },
  afterPageLoad: (run) => {
    if (hasWindow) afterPageLoad(run, window)
  },
  prewarm: hasWindow && window.location.hostname === 'fridge.com',
  events: hasWindow ? window : undefined,
  isHidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
  sendBeacon: hasWindow && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function' ? (url, body) => navigator.sendBeacon(url, body) : undefined,
  pageContext: () =>
    hasWindow ? { $current_url: window.location.href, $host: window.location.host, $pathname: window.location.pathname, title: document.title } : {},
  storage: storage('localStorage'),
  sessionStorage: storage('sessionStorage'),
  cookie: () => (typeof document === 'undefined' ? '' : document.cookie),
  libVersion: typeof __POSTHOG_JS_VERSION__ === 'string' ? __POSTHOG_JS_VERSION__ : 'unknown',
  onInitialised: () => announcePostHogLoaded(),
})

/**
 * One client per page. The build can evaluate this file in more than one module: Turbopack
 * merges it with its imports into a second module for the chunk that sentry.client.config.ts
 * imports `posthog-js` from. The provider must init the very client Sentry's beforeSend reads,
 * and a second client would download the library again, so the first copy to run keeps its
 * client on window and every later copy returns that one.
 */
const CLIENT_KEY = '__fkDeferredPostHog'
const holder = hasWindow ? (window as unknown as Record<string, DeferredPostHog | undefined>) : null
const posthog: DeferredPostHog = holder?.[CLIENT_KEY] ?? createClient()
if (holder && !holder[CLIENT_KEY]) holder[CLIENT_KEY] = posthog

export default posthog
export { posthog }
